import Cookies from 'js-cookie';
import { useMemo } from 'react';
import { REFERRAL_COOKIE_NAME, ReferralCookieData } from '../lib/util/referralCookie';
import { cartActions as storeCartActions } from '../reducers/storeCart';
import { applyReferralCodeToCart, isValidCodeResult } from '../lib/promoCode';
import { ReferralSource } from '../types/gql-op-types';
import useApplyPromotionFromCookie from './useApplyPromotionFromCookie';

function parseReferralSourceFromCookie(source: string | undefined): ReferralSource {
  switch (source) {
    case ReferralSource.Onboarding:
      return ReferralSource.Onboarding;
    case ReferralSource.ReferralsTab:
      return ReferralSource.ReferralsTab;
    default:
      return ReferralSource.DirectLink;
  }
}

export default function useAutoApplyReferral() {
  const { finished } = useApplyPromotionFromCookie<ReferralCookieData>(
    useMemo(
      () => ({
        getCookieValue: () => {
          const dataValue = Cookies.getJSON(REFERRAL_COOKIE_NAME);
          if (dataValue) {
            return [
              dataValue,
              () => {
                /* Noop */
              },
            ];
          }

          return undefined;
        },

        applyPromotion: async ({ value, dispatch, cart }) => {
          const result = await applyReferralCodeToCart(cart, value.code);

          // When auto applying referral, we only don't want to do it if the referral is not a valid code.
          // If it's other errors (e.g. "Not applicable to any items in cart") then we still want to apply it.
          if (isValidCodeResult(result)) {
            const referralDetails = result.kind === 'success' ? result.value : undefined;
            dispatch(
              storeCartActions.addReferral({
                code: value.code,
                referrerName: referralDetails?.referrerName ?? value.name,
                referrerAvatarUrl: referralDetails?.referrerAvatarUrl,
                referrerPetAvatarUrl: referralDetails?.referrerPetAvatarUrl,
                source: parseReferralSourceFromCookie(value.source),
              }),
            );
          } else {
            Cookies.remove(REFERRAL_COOKIE_NAME);
          }
        },
      }),
      [],
    ),
  );

  return { finished };
}
